import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  List,
  ListItem,
  Select,
  Text,
  VStack
} from '@chakra-ui/react';
import { MdDeleteOutline, MdDragIndicator, MdOutlineAdd } from 'react-icons/md';

function ArrayDragDropManager({
  availableOptions,
  value = [],
  onChange,
  label = 'Items',
  placeholder = 'Select an item',
  emptyMessage = 'No items selected.',
  hideLabel = true
}) {
  const [selectedItem, setSelectedItem] = useState('');
  const [addedItems, setAddedItems] = useState(value || []);

  // Convert options to simple array if they have key/value structure
  const itemOptions = availableOptions.map((option) =>
    typeof option === 'string' ? option : option.value || option.key
  );

  // Handle adding an item
  const addItem = () => {
    if (selectedItem && !addedItems.includes(selectedItem)) {
      const newItems = [...addedItems, selectedItem];
      setAddedItems(newItems);
      setSelectedItem(''); // Reset the dropdown
      onChange && onChange(newItems);
    }
  };

  // Handle deleting an item
  const deleteItem = (item) => {
    const newItems = addedItems.filter((i) => i !== item);
    setAddedItems(newItems);
    onChange && onChange(newItems);
  };

  // Handle drag-and-drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return; // If dropped outside the list, do nothing

    const items = Array.from(addedItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setAddedItems(items);
    onChange && onChange(items);
  };

  const memoizedItems = useMemo(() => addedItems, [addedItems]);

  // Update local state when value prop changes
  useEffect(() => {
    if (value && Array.isArray(value)) {
      setAddedItems(value);
    }
  }, [value]);

  return (
    <Box>
      <VStack spacing={0} mb={4}>
        {!hideLabel && (
          <Flex
            w="100%"
            justifyContent="flex-start"
            height="100%"
            alignItems="center"
          >
            <FormLabel
              alignSelf="flex-end"
              fontSize="0.75rem"
              fontWeight="500"
              mb={0}
              lineHeight="2rem"
              pr={2}
              size={'md'}
              htmlFor={'array-items'}
            >
              {label}
            </FormLabel>
          </Flex>
        )}
        <HStack gap={2}>
          <Select
            placeholder={placeholder}
            value={selectedItem}
            onChange={(e) => setSelectedItem(e.target.value)}
          >
            {itemOptions.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </Select>
          <Button
            colorScheme="brand"
            size={'md'}
            onClick={addItem}
            isDisabled={!selectedItem}
            rightIcon={<MdOutlineAdd />}
          >
            Add
          </Button>
        </HStack>
      </VStack>
      {/* Drag-and-Drop List */}
      <Box
        borderRadius="10px"
        border="1px solid"
        borderColor="whiteAlpha.300"
        maxHeight="24rem"
        overflow="auto"
        p={4}
      >
        {addedItems.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="items-list">
              {(provided) => (
                <List
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  spacing={2}
                >
                  {addedItems.map((item, index) => (
                    <Draggable key={item} draggableId={item} index={index}>
                      {(provided) => (
                        <ListItem
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          border="1px solid"
                          borderColor="whiteAlpha.300"
                          borderRadius="xl"
                          bgColor={'whiteAlpha.300'}
                          p={2}
                        >
                          <HStack>
                            <MdDragIndicator size={'1.25rem'} />
                            <Text>
                              {index + 1}. {item}
                            </Text>
                          </HStack>
                          <IconButton
                            icon={<MdDeleteOutline />}
                            colorScheme={'red'}
                            size="sm"
                            bgColor="whiteAlpha.300"
                            border="1px solid"
                            borderColor="whiteAlpha.300"
                            borderRadius="full"
                            p={0}
                            onClick={() => deleteItem(item)}
                            aria-label={`Delete ${item}`}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <Text color="yellow.500" size="sm" align="center">
            {emptyMessage}
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default ArrayDragDropManager;
